import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Checkbox,
  Alert,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useNavigate } from 'react-router-dom';
import { OrdinaryButton } from '../../../../common/Buttons';
import { usePropertyId } from '../../../../common/hooks/usePropertyId';
import { useReservationsData } from '../../../../common/hooks/useReservationsData';
import {
  PAYMENT_METHOD_BANK_TRANSFER,
  PAYMENT_METHOD_PAYU,
  formatDate,
  formatPrice,
  sortPaymentsByDueDate,
} from './reservationUtils';

const PAYMENT_METHODS = [
  {
    value: PAYMENT_METHOD_BANK_TRANSFER,
    label: 'Przelew bankowy',
    description:
      'Opłać rezerwację przelewem na konto bankowe. Dane do przelewu otrzymasz na stronie potwierdzenia.',
    icon: <AccountBalanceIcon />,
  },
  {
    value: PAYMENT_METHOD_PAYU,
    label: 'PayU',
    description: 'Zapłać kartą, BLIKiem lub inną metodą przez PayU.',
    icon: <CreditCardIcon />,
  },
];

const ReservationPayment = ({ reservation, payments = [] }) => {
  const propertyId = usePropertyId();
  const { checkout } = useReservationsData(propertyId, reservation.id);
  const navigate = useNavigate();

  const unpaidPayments = useMemo(
    () => sortPaymentsByDueDate(payments.filter((p) => !p.paidDate)),
    [payments]
  );

  const [selectedPayuPaymentIds, setSelectedPayuPaymentIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [localPaymentMethod, setLocalPaymentMethod] = useState(PAYMENT_METHOD_BANK_TRANSFER);

  useEffect(() => {
    if (unpaidPayments.length > 0) {
      setSelectedPayuPaymentIds([unpaidPayments[0].id]);
    } else {
      setSelectedPayuPaymentIds([]);
    }
  }, [reservation?.id, unpaidPayments]);

  const handleMethodChange = useCallback((event) => {
    setLocalPaymentMethod(event.target.value);
    setLocalError(null);
  }, []);

  const handlePayuPaymentToggle = useCallback((paymentId, index) => {
    if (index === 0) return;
    setSelectedPayuPaymentIds((prev) =>
      prev.includes(paymentId)
        ? prev.filter((id) => id !== paymentId)
        : [...prev, paymentId]
    );
  }, []);

  const paymentsDueNow = useMemo(() => {
    if (unpaidPayments.length === 0) return [];
    if (localPaymentMethod === PAYMENT_METHOD_PAYU) {
      return sortPaymentsByDueDate(
        unpaidPayments.filter((p) => selectedPayuPaymentIds.includes(p.id))
      );
    }
    return [unpaidPayments[0]];
  }, [localPaymentMethod, unpaidPayments, selectedPayuPaymentIds]);

  const amountDueNow = useMemo(
    () => paymentsDueNow.reduce((sum, p) => sum + (Number(p.amountDue) || 0), 0),
    [paymentsDueNow]
  );

  const displayError = localError;
  const showPayuSelection =
    localPaymentMethod === PAYMENT_METHOD_PAYU && unpaidPayments.length > 1;

  const handleSubmit = async () => {
    if (submitting || !reservation.id) return;
    setLocalError(null);
    setSubmitting(true);
    try {
      const paymentIds =
        localPaymentMethod === PAYMENT_METHOD_PAYU && selectedPayuPaymentIds.length > 0
          ? sortPaymentsByDueDate(
              unpaidPayments.filter((p) => selectedPayuPaymentIds.includes(p.id))
            ).map((p) => p.id)
          : undefined;

      const { paymentRedirectUrl } = await checkout(
        reservation.id,
        localPaymentMethod,
        paymentIds
      );

      if (localPaymentMethod === PAYMENT_METHOD_PAYU) {
        if (!paymentRedirectUrl) {
          setLocalError('Nie udało się uruchomić płatności PayU. Spróbuj ponownie.');
          return;
        }
        window.location.href = paymentRedirectUrl;
        return;
      }

      navigate(`/reservation/${reservation.id}`, { replace: true });
    } catch (err) {
      setLocalError(err?.message ?? 'Nie udało się przetworzyć płatności.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      {unpaidPayments.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Do zapłaty
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {formatPrice(amountDueNow)}
          </Typography>
          {localPaymentMethod === PAYMENT_METHOD_PAYU && paymentsDueNow.length > 1 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {paymentsDueNow.length} płatności w jednej transakcji PayU
            </Typography>
          )}
        </Paper>
      )}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Metoda płatności</Typography>

      {displayError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError(null)}>
          {displayError}
        </Alert>
      )}

      <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
        <RadioGroup
          name="paymentMethod"
          value={localPaymentMethod}
          onChange={handleMethodChange}
          sx={{ gap: 1 }}
        >
          {PAYMENT_METHODS.map((method) => (
            <Paper
              key={method.value}
              variant="outlined"
              sx={{
                p: 2,
                breservationRadius: 2,
                cursor: submitting ? 'default' : 'pointer',
                breservation: 2,
                breservationColor: localPaymentMethod === method.value ? 'primary.main' : 'divider',
                bgcolor: localPaymentMethod === method.value ? 'action.selected' : 'background.paper',
                transition: 'breservation-color 0.2s, background-color 0.2s',
                opacity: submitting ? 0.7 : 1,
                '&:hover': submitting ? {} : {
                  breservationColor: localPaymentMethod === method.value ? 'primary.main' : 'action.hover',
                },
              }}
              onClick={() => !submitting && handleMethodChange({ target: { value: method.value } })}
            >
              <FormControlLabel
                value={method.value}
                control={<Radio disabled={submitting} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Box sx={{ color: 'primary.main', mt: 0.25 }}>{method.icon}</Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>{method.label}</Typography>
                      <Typography variant="body2" color="text.secondary">{method.description}</Typography>
                    </Box>
                  </Box>
                }
                sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
              />
            </Paper>
          ))}
        </RadioGroup>
      </FormControl>

      {showPayuSelection && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, breservationRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Wybierz płatności do opłacenia przez PayU
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pierwsza płatność jest obowiązkowa. Możesz dodatkowo opłacić kolejne raty jedną transakcją PayU.
          </Typography>
          {unpaidPayments.map((p, index) => {
            const checked = selectedPayuPaymentIds.includes(p.id);
            const mandatory = index === 0;
            return (
              <Box
                key={p.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  breservationBottom: index < unpaidPayments.length - 1 ? '1px solid' : 'none',
                  breservationColor: 'divider',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked}
                      disabled={submitting || mandatory}
                      onChange={() => handlePayuPaymentToggle(p.id, index)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {formatDate(p.dueDate)} — {formatPrice(p.amountDue)}
                      </Typography>
                      {mandatory && (
                        <Typography variant="caption" color="text.secondary">
                          Obowiązkowa
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </Box>
            );
          })}
        </Paper>
      )}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <OrdinaryButton
          text="Dalej"
          onClick={handleSubmit}
          disabled={submitting || unpaidPayments.length === 0}
          loading={submitting}
        />
      </Box>
    </Box>
  );
};

export default ReservationPayment;
